import React from "react";
import { ApplicationStatus } from "../../interfaces/enums";
import { Chip, useTheme } from "@mui/material";
import { getDaysLeftStatus, isDateEnded } from "../../utils/dates";
import { getStatusLabel } from "../../utils/statusTranslations";

interface StatusChipProps {
  type: "contest" | "application";
  value: Date | ApplicationStatus;
  size?: "small" | "medium";
}

export default function StatusChip({ type, value, size = "small" }: StatusChipProps) {
  const theme = useTheme();

  if (type === "contest") {
    const endDate = value as Date;

    if (isDateEnded(endDate)) {
      return (
        <Chip
          label="Zakończone"
          size={size}
          sx={{
            backgroundColor: theme.palette.grey[300],
            color: theme.palette.grey[700],
            fontSize: "0.75rem",
          }}
        />
      );
    }

    const status = getDaysLeftStatus(endDate);
    const colorMap = {
      urgent: { bg: theme.palette.error.light, color: theme.palette.error.contrastText },
      warning: { bg: theme.palette.warning.light, color: theme.palette.warning.contrastText },
      normal: { bg: theme.palette.success.light, color: theme.palette.success.contrastText },
    } as const;

    const colors = colorMap[status.type as keyof typeof colorMap];

    return (
      <Chip
        label={`${status.days} dni`}
        size={size}
        sx={{
          backgroundColor: colors.bg,
          color: colors.color,
          fontSize: "0.75rem",
        }}
      />
    );
  }

  // Application status
  const status = value as ApplicationStatus;
  const statusColors = {
    [ApplicationStatus.DRAFT]: { bg: "#fef3c7", color: "#92400e" }, // W PRZYGOTOWANIU - żółty
    [ApplicationStatus.SENT]: { bg: "#dbeafe", color: "#1e40af" }, // WYSŁANE - niebieski
    [ApplicationStatus.APPROVED]: { bg: "#d1fae5", color: "#065f46" }, // ZAAKCEPTOWANE - zielony
    [ApplicationStatus.REJECTED]: { bg: "#fee2e2", color: "#991b1b" }, // ODRZUCONE - czerwony
    [ApplicationStatus.CANCELED]: { bg: "#f3f4f6", color: "#374151" }, // ANULOWANE - szary
    [ApplicationStatus.MODERATION]: { bg: "#fef3c7", color: "#92400e" }, // W MODERACJI - żółty
  };

  const colors = statusColors[status] || { bg: "#f3f4f6", color: "#374151" };

  return (
    <Chip
      label={getStatusLabel(status)}
      size={size}
      sx={{
        backgroundColor: colors.bg,
        color: colors.color,
        fontSize: "0.75rem",
        fontWeight: 500,
      }}
    />
  );
}
